import React, { useEffect, useState } from 'react';
import AxiosCaller from '../../../authenticatie/AxiosCaller';
import PDFDownloader from '../../../Components/PDFDownloader/PDFDownloader';
import { DownOutlined, UpOutlined, FilePdfOutlined } from '@ant-design/icons';
import { 
  Card, 
  Button, 
  Table, 
  DatePicker, 
  Select, 
  Typography, 
  Space, 
  Divider, 
  Row, 
  Col, 
  message,
  Modal,
  Input
} from 'antd';
import dayjs from 'dayjs';

import { openNotificationSuccess, openNotificationError } from '../../../Components/Notification/Notification';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const BestellingsGeschiedenisScreen = () => {
  const { get, post, put } = AxiosCaller();

  const [geschiedenis, setGeschiedenis] = useState([]);
  const [expandedCardIndex, setExpandedCardIndex] = useState(null);
  const [bestellen, setBestellen] = useState([]);
  const [datum, setDatum] = useState(null);
  const [leverancier, setLeverancier] = useState(null);
  const [leveranciers, setLeveranciers] = useState([]);
  const [pdfModalVisible, setPdfModalVisible] = useState(false);
  const [selectedBestelling, setSelectedBestelling] = useState(null);
  const [bestandsnaam, setBestandsnaam] = useState('');
  const [opmerking, setOpmerking] = useState('');

  const [maxHeight, setMaxHeight] = useState(0);
  
  useEffect(() => {
    const updateHeight = () => {
      setMaxHeight(0.7 * document.documentElement.clientHeight);
    };

    updateHeight();
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, []);

  useEffect(() => {
    fetchBestelGeschiedenis();
    fetchLeveranciers();
  }, []);

  useEffect(() => {
    fetchBestelGeschiedenis();
  }, [datum, leverancier]);

  const fetchBestelGeschiedenis = async () => {
    try {
      let date = null;

      if (datum) {
        date = dayjs(datum).utc(true)
      }
      
      const response = await post('/bestellingsGeschiedenis/filtered', {
        datum: date, 
        leverancier: leverancier
      });

      if (Array.isArray(response.data)) {
        filterIsBesteld(response.data);
      } else {
        openNotificationError("Foute data opgehaalt")
      }
    } catch (error) {
      console.error(error);
      openNotificationError('Fout bij ophalen bestelgeschiedenis', error)
    }
  };

  const fetchLeveranciers = async () => {
    try {
      const response = await get('/leverancier');
     
      if (Array.isArray(response.data)) {
        setLeveranciers(response.data);
      } else {
        openNotificationError("Foute data opgehaalt")
      }
    } catch (error) {
      console.error(error);
      openNotificationError('Fout bij ophalen van leveranciers', error)
    }
  };

  const filterIsBesteld = (data) => {
    const sortedData = [...data].sort((a, b) => {
      const dateA = new Date(a.isBesteld ? a.bestelDatum : a.goedkeuringsDatum);
      const dateB = new Date(b.isBesteld ? b.bestelDatum : b.goedkeuringsDatum);
      return dateB - dateA;
    });
    
    setGeschiedenis(sortedData.filter((bestelling) => bestelling.isBesteld === true));
    setBestellen(sortedData.filter((bestelling) => bestelling.isBesteld === false));
  };

  const toggleCard = (index) => {
    setExpandedCardIndex(expandedCardIndex === index ? null : index);
  };

  const bestel = async (bestelling) => {
    try {
      const response = await post(`/bestellingsGeschiedenis/bestel/${bestelling._id}`);
      
      await fetchBestelGeschiedenis();

      if (response.data.bestelling.isBesteld) {
        openNotificationSuccess('Bestelling succesvol verwerkt.');
      }
    } catch (error) {
      console.error(error);
      openNotificationError('Fout bij verwerken van bestelling', error)
    }
  };

  const generateDefaultFilename = (bestelling) => {
    const date = dayjs(bestelling.bestelDatum).format('DD-MM-YYYY');
    const leverancier = bestelling.leverancier.replace(/\s+/g, '_');
    return `bestelling_${leverancier}_${date}`;
  };

  const openPdfModal = (bestelling) => {
    setSelectedBestelling(bestelling);
    setBestandsnaam(generateDefaultFilename(bestelling));
    setOpmerking(bestelling.opmerking || '');
    setPdfModalVisible(true);
  };

  const handlePdfGeneration = async () => {
    if (!bestandsnaam.trim()) {
      message.error('Voer een bestandsnaam in');
      return;
    }

    if (selectedBestelling) {
      try {
        // If there's an opmerking, update the bestelling in the database
        if (opmerking.trim() && opmerking !== selectedBestelling.opmerking) {
          const updatedBestelling = {
            opmerking: opmerking.trim()
          };
          
          await put(`/bestellingsGeschiedenis/${selectedBestelling._id}`, updatedBestelling);
          await fetchBestelGeschiedenis(); // Refresh the data
        }

        // Generate PDF with the opmerking
        genereerPDF(
          { ...selectedBestelling, opmerking: opmerking.trim() }, 
          bestandsnaam
        );
        
        setPdfModalVisible(false);
        setBestandsnaam('');
        setOpmerking('');
        setSelectedBestelling(null);
        message.success('PDF wordt gegenereerd...');
      } catch (error) {
        console.error('Error updating bestelling:', error);
        openNotificationError('Fout bij bijwerken van opmerking', error);
      }
    }
  };

  const handlePdfModalCancel = () => {
    setPdfModalVisible(false);
    setBestandsnaam('');
    setOpmerking('');
    setSelectedBestelling(null);
  };

  const genereerPDF = (bestelling, bestandsnaam) => {
    const producten = [];
    
    if (bestelling.isGesplitst) {
      bestelling.producten.forEach((product) => {
        producten.push({
          klas: product.klas,
          leerkracht: product.leerkracht,
          product: product.product,
          datum: product.kookDatum,
          aantal: product.aantal,
          eenheid: product.eenheid,
          leverancier: bestelling.leverancier,
        });
      });

      const data = {
        datum: bestelling.bestelDatum,
        leverancier: bestelling.leverancier,
        producten: producten,
        opmerking: bestelling.opmerking || '',
      };

      PDFDownloader.generateVleesPDF(data, bestandsnaam);
    } else {
      bestelling.producten.forEach((product) => {
        producten.push({
          product: product.product,
          aantal: product.aantal,
          eenheid: product.eenheid,
          leverancier: bestelling.leverancier,
          datum: bestelling.Datum,
        });
      });
      
      const data = {
        datum: bestelling.bestelDatum,
        leverancier: bestelling.leverancier,
        producten: producten,
        opmerking: bestelling.opmerking || '',
      };
      
      PDFDownloader.generateProductPDF(data, bestandsnaam);
    }
  };

  return (
    <div className="Screen-container">
      <h1 className='title'>Bestellingsgeschiedenis</h1>
      
      {/* PDF Filename Modal */}
      <Modal
        title="Bestelling Details"
        open={pdfModalVisible}
        onOk={handlePdfGeneration}
        onCancel={handlePdfModalCancel}
        okText="PDF Genereren"
        cancelText="Annuleren"
        centered
        width={600}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <div>
            <Text strong>Bestandsnaam voor PDF:</Text>
            <Input
              placeholder="Bestandsnaam"
              value={bestandsnaam}
              onChange={(e) => setBestandsnaam(e.target.value)}
              onPressEnter={handlePdfGeneration}
              suffix=".pdf"
              style={{ marginTop: '8px' }}
            />
            <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: '4px' }}>
              De bestandsnaam wordt automatisch aangevuld met .pdf
            </Text>
          </div>
          
          <div>
            <Text strong>Opmerking (optioneel):</Text>
            <TextArea
              placeholder="Voeg een opmerking toe aan de bestelling..."
              value={opmerking}
              onChange={(e) => setOpmerking(e.target.value)}
              rows={4}
              style={{ marginTop: '8px' }}
              maxLength={500}
            />
            <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: '4px' }}>
              {opmerking.length}/500 karakters
            </Text>
          </div>

          {selectedBestelling && (
            <div style={{ background: '#f5f5f5', padding: '12px', borderRadius: '6px' }}>
              <Text strong>Bestelling overzicht:</Text>
              <div style={{ marginTop: '8px' }}>
                <Text type="secondary">Leverancier: </Text>
                <Text>{selectedBestelling.leverancier}</Text>
              </div>
              <div>
                <Text type="secondary">Aantal producten: </Text>
                <Text>{selectedBestelling.producten?.length || 0}</Text>
              </div>
              <div>
                <Text type="secondary">Datum: </Text>
                <Text>{dayjs(selectedBestelling.bestelDatum).format('DD/MM/YYYY')}</Text>
              </div>
            </div>
          )}
        </Space>
      </Modal>

      <FilterContainer 
        datum={datum}
        setDatum={setDatum}
        leverancier={leverancier}
        setLeverancier={setLeverancier}
        leveranciers={leveranciers}
      />
      
      <Space direction="vertical" size="middle" style={{ width: '100%', overflowY: "auto", maxHeight, marginTop: '16px' }}>
        {geschiedenis.map((item, index) => (
          <Bestelling
            key={index}
            bestelling={item}
            isExpanded={expandedCardIndex === index}
            onClick={() => toggleCard(index)}
            bestel={bestel}
            isBesteld={true}
            openPdfModal={openPdfModal}
          />
        ))}
      </Space>
    </div>
  );
};

const FilterContainer = ({ datum, setDatum, leverancier, setLeverancier, leveranciers }) => {
  const disableFutureDates = (current) => {
    return current && current > dayjs().startOf('day');
  };

  return (
      <Row gutter={24}>
        <Col span={12}>
          <DatePicker 
            style={{ width: '100%' }}
            value={datum ? dayjs(datum) : null}
            onChange={(date) => setDatum(date ? date.format('YYYY-MM-DD') : null)}
            placeholder="Selecteer een datum"
            format="DD/MM/YYYY"
            disabledDate={disableFutureDates}
          />
        </Col>
        
        <Col span={12}>
          <Select
            style={{ width: '100%' }}
            placeholder="Selecteer een leverancier"
            value={leverancier}
            onChange={(value) => setLeverancier(value)}
            showSearch
            optionFilterProp="children"
            allowClear
          >
            {leveranciers.map((lev, index) => (
              <Option key={index} value={lev.naam}>{lev.naam}</Option>
            ))}
          </Select>
        </Col>
      </Row>
  );
};

const Bestelling = ({
  bestelling,
  isExpanded,
  onClick,
  bestel,
  isBesteld,
  openPdfModal,
}) => {
  
  const columns = [
    ...(bestelling.isGesplitst ? [
      {
        title: 'KookDatum',
        dataIndex: 'kookDatum',
        key: 'kookDatum',
        render: (text) => text ? dayjs(text).format('YYYY-MM-DD') : '-',
      },
      {
        title: 'Leerkracht',
        dataIndex: 'leerkracht',
        key: 'leerkracht',
      }
    ] : []),
    {
      title: 'Product',
      dataIndex: 'product',
      key: 'product',
    },
    {
      title: 'Aantal',
      key: 'aantalEenheid',
      align: 'right',
      render: (_, record) => `${record.aantal} ${record.eenheid}`
    }
  ];

  return (
    <Card 
      style={{ width: '100%' }}
      bodyStyle={{ padding: isExpanded ? '16px' : '12px 16px' }}
    >
      <Row justify="space-between" align="middle">
        <Col>
          <Space direction="vertical" size="small">
            <Space>
              <Text strong>{isBesteld ? 'Bestel Datum:' : 'Goedkeuringsdatum:'}</Text>
              <Text>
                {isBesteld
                  ? dayjs(bestelling.bestelDatum).format('DD/MM/YYYY')
                  : dayjs(bestelling.goedkeuringsDatum).format('DD/MM/YYYY')}
              </Text>
            </Space>
            <Space>
              <Text strong>Leverancier:</Text>
              <Text>{bestelling.leverancier}</Text>
            </Space>
            {bestelling.opmerking && (
              <Space>
                <Text strong>Opmerking:</Text>
                <Text type="secondary" style={{ fontStyle: 'italic' }}>
                  {bestelling.opmerking}
                </Text>
              </Space>
            )}
          </Space>
        </Col>
        
        <Col>
          <Space>
            <Button 
              type={isBesteld ? 'default' : 'primary'}
              onClick={() => bestel(bestelling)}
            >
              {isBesteld ? 'Terugzetten' : 'Bestel'}
            </Button>
            
            {isBesteld && (
              <Button 
                type="primary" 
                icon={<FilePdfOutlined />}
                onClick={() => openPdfModal(bestelling)}
              >
                Download PDF
              </Button>
            )}
          </Space>
        </Col>
      </Row>
      
      {isExpanded && (
        <div style={{ marginTop: '16px' }}>
          <Table 
            dataSource={bestelling.producten} 
            columns={columns}
            rowKey={(record, index) => index}
            pagination={false}
            size="middle"
            bordered
            locale={{ emptyText: 'Geen producten gevonden voor deze bestelling.' }}
          />
        </div>
      )}
      
      <Divider style={{ margin: '8px 0' }} />
      
      <div 
        style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          cursor: 'pointer',
          color: '#1890ff'
        }}
        onClick={onClick}
      >
        {!isExpanded ? (
          <Space>
            <Text>Meer info</Text>
            <DownOutlined />
          </Space>
        ) : (
          <Space>
            <Text>Minder info</Text>
            <UpOutlined />
          </Space>
        )}
      </div>
    </Card>
  );
};

export default BestellingsGeschiedenisScreen;